/**
 * 📜 OnboardingCariLectureModal — full-screen story moment when the user
 * makes their FIRST deposit to the GrainHouse Vault.
 *
 * Multi-step Cari monologue. She lectures about hiding savings. She slowly
 * walks away (the modal dims her silhouette). Then comes back with an old
 * family journal she found in her attic, presses it into the hero's hands.
 * On accept → POST /api/onboarding/cari/seen → stage advances → quest begins.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Locked dialog — Cari's "secure your savings" lecture, in 6 beats.
const BEATS = [
  {
    icon: "😻",
    pose: "lecture",
    title: "GENERAL CARI",
    body: (
      <>
        Ahhh, you've stashed your first Chits in the Vault. 🌾 Smart Phoenix.
        <br /><br />
        Listen carefully now. In Dragonious, your wallet is <b>fair game</b>.
        Bandits raid. Tax-collectors visit. Some pretender will come knocking
        with a sob story and leave with your savings.
      </>
    ),
  },
  {
    icon: "😻",
    pose: "lecture",
    title: "GENERAL CARI",
    body: (
      <>
        The GrainHouse is your <b>safe</b>. The overflow wallet is your <b>bait</b>.
        Spend from the bait. Hide your savings.
        <br /><br />
        Every old Phoenix who ever lived past their first molt learned this:
        if you can <i>see</i> your money, so can someone else.
      </>
    ),
  },
  {
    icon: "😻",
    pose: "lecture",
    title: "GENERAL CARI",
    body: (
      <>
        Hide. Hoard. Skim. The trick to wealth in this kingdom isn't <i>making</i>
        Chits — it's <b>making them disappear</b> from prying eyes.
        <br /><br />
        Don't trust the diner cook with your purse. Don't trust the gym owner
        with your purse. Don't trust the Police Chief with — well, with
        anything. *sighs*
      </>
    ),
  },
  {
    icon: "🚶‍♀️",
    pose: "walking_away",
    title: "GENERAL CARI",
    body: (
      <>
        That's all the wisdom I have for one afternoon, sweetling.
        <br /><br />
        *Cari turns and slowly walks away, mumbling about tomes and tea*
        <br /><br />
        <span className="text-neutral-500 italic">...wait. Where is she going?</span>
      </>
    ),
  },
  {
    icon: "🤔",
    pose: "returning",
    title: "GENERAL CARI",
    body: (
      <>
        *Several beats pass. The door creaks open again. Cari is back, holding
        something dusty and leather-bound.*
        <br /><br />
        Oh — oh dear, before I forget. I found this in my attic when I moved
        in. An old <b>family journal</b>. I've tried to read it for ages, but
        the script is...
      </>
    ),
  },
  {
    icon: "📜",
    pose: "handoff",
    title: "GENERAL CARI",
    body: (
      <>
        ...the language isn't mine. I can't make heads or tails of it.
        <br /><br />
        You take it. Ask around town. Someone with younger eyes — or hungrier
        ones — might know what it says.
        <br /><br />
        <b className="text-amber-300">📜 Cari's Family Journal</b> has been
        added to your Quest Items.
      </>
    ),
  },
];

export default function OnboardingCariLectureModal({ onComplete }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const beat = BEATS[step];
  const isLast = step >= BEATS.length - 1;
  const isWalking = beat.pose === "walking_away";
  const isReturning = beat.pose === "returning";
  const isHandoff = beat.pose === "handoff";

  // ESC = SKIP guarded (only after step 2 — the lecture is locked, the
  // handoff is skippable to avoid trapping the user).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" && !busy) {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, busy]);

  const next = async () => {
    if (busy) return;
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    // Final beat — POST and dismiss
    setBusy(true);
    try {
      await axios.post(`${API}/onboarding/cari/seen`);
      toast.success("📜 You received Cari's Family Journal.");
      if (onComplete) onComplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept the journal.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gradient-to-br from-black/95 via-[#10001a]/95 to-black/95 backdrop-blur"
      data-testid="onboarding-cari-modal"
    >
      <div className="relative w-full max-w-3xl">
        {/* Cari portrait — opacity fades when she "walks away" */}
        <div
          className={`absolute -top-20 left-1/2 -translate-x-1/2 text-[120px] select-none transition-all duration-1000 ${
            isWalking ? "opacity-30 translate-x-[40%]" :
            isReturning ? "opacity-90 -translate-x-[60%]" :
            "opacity-100"
          }`}
          style={{ filter: isWalking ? "blur(2px)" : "none" }}
        >
          {beat.icon}
        </div>

        <div className="mt-16 rounded-2xl border-2 border-purple-700/60 bg-gradient-to-br from-[#1a0010] via-black to-[#10001a] p-8 shadow-[0_0_60px_rgba(168,85,247,0.25)]">
          <div className="text-[10px] uppercase tracking-[0.5em] text-purple-300/70 mb-1 text-center">
            📚 The Library · Step {step + 1} / {BEATS.length}
          </div>
          <h2
            className="text-3xl md:text-4xl font-black text-amber-200 mb-5 text-center"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            {beat.title}
          </h2>

          <div
            data-testid={`cari-beat-${step}`}
            className="text-base md:text-lg text-amber-100/95 leading-relaxed font-serif min-h-[180px]"
          >
            {beat.body}
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="flex gap-1">
              {BEATS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i < step ? "w-3 bg-amber-500" :
                    i === step ? "w-8 bg-amber-300" :
                    "w-3 bg-purple-900"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              disabled={busy}
              data-testid="cari-next-btn"
              className="px-6 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm tracking-wide"
            >
              {busy
                ? "Accepting..."
                : isLast
                  ? "Take the Journal →"
                  : isHandoff
                    ? "Take it →"
                    : "Next →"}
            </button>
          </div>

          {step === 0 && (
            <div className="text-[10px] text-neutral-500 text-center mt-3 italic">
              Press Enter to continue · this only happens once
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
